import theme from '@assets/theme';
import {
  useFetchLicenseDataAndSetStoreValues,
  useFetchWhoami,
} from '@http/queries';
import {
  ThemeProvider,
  Alert,
  Box,
  CssBaseline,
  IconButton,
  Snackbar,
} from '@mui/material';
import * as Sentry from '@sentry/browser';
import useWindowSize from '@hooks/useWindowSize';
import { IconX } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import 'chartjs-adapter-date-fns';
import { fromUnixTime } from 'date-fns';
import { useAtom } from 'jotai';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useMatches } from 'react-router-dom';
import {
  demoModeAtom,
  rerenderLayoutAtom,
  toastsAtom,
  tokenExpiresAtom,
  userAtom,
} from './global-store';
import clearStorageWithExeptions from './helpers/clearStorageWithExeptions';
import { MainLayout } from '@layout/MainLayout';
import { usePathname } from '@/hooks/usePathname';
import PrivacyDialog from './pages/authentication/PrivacyDialog';
import { SidebarProvider } from '@layout/Sidebar/Sidebar';
import { FaceliftWelcomeBanner } from '@layout/FaceliftWelcomeBanner.tsx';

export function isModuleRestricted(user, _routePath, allowedConfig) {
  if (!allowedConfig?.module) return false;
  return (
    user.restrictedModules?.length &&
    user.restrictedModules.includes(allowedConfig.module)
  );
}

function checkIfRouteIsAllowed(user, matches, demoMode) {
  for (let match of matches) {
    let { allowed, allowedConfig } = match.handle ?? {};

    if (!allowed) {
      console.warn('Route Item has no allowed property', match);
      continue;
    }

    if (!Array.isArray(allowed)) {
      allowed = [allowed];
    }

    for (let allowedItem of allowed) {
      if (allowedItem === 'all') continue; // allowed for all users

      if (user) {
        if (allowedItem === 'no-user') {
          console.warn('NO-USER-Route has user', user, match);
          return demoMode ? '/demo' : '/';
        }

        if (allowedItem === 'admin' && !user.isAdmin) {
          console.warn('ADMIN-Route user is not admin', user, match);
          return demoMode ? '/demo' : '/';
        }

        if (allowedItem === 'restrict') {
          if (isModuleRestricted(user, match.pathname, allowedConfig)) {
            console.warn('Trying to access RESTRICT route', user, match);
            return demoMode ? '/demo' : '/';
          }
        }

        if (allowedItem === 'no-demo' && demoMode) return '/demo';
      } else {
        // no user, only allowed to access 'no-user' routes
        if (allowedItem !== 'no-user') {
          console.warn('Trying to access USER route without user', user, match);
          return '/authentication/login';
        }
      }
    }
  }
}

function Notification({ notification, onExited, ...props }) {
  const [open, setOpen] = useState(true);

  function handleClose(_event, reason) {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  }

  if (!notification?.message) return null;

  return (
    <Snackbar
      TransitionProps={{ onExited }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={parseInt(notification.duration) || 7000}
      key={notification.key}
      onClose={handleClose}
      open={open}
      {...props}
    >
      <Alert
        severity={notification.severity}
        sx={{
          border: '2px solid black',
          fontWeight: 600,
          color: theme.palette.black.main,
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'nowrap',
          '& .MuiAlert-message': {
            flexShrink: 1,
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        <Box aria-live='polite'>{notification.message}</Box>

        <IconButton onClick={handleClose} size='small' sx={{ ml: 2 }}>
          <IconX />
        </IconButton>
      </Alert>
    </Snackbar>
  );
}

function BaseRoute() {
  const { i18n, ready } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [langSetup, setLangSetup] = useState(false);
  const [renderKey, setRenderKey] = useAtom(rerenderLayoutAtom);

  const queryClient = useQueryClient();

  const { isLoading: LicenseDataLoading, isError: LicenseDataError } =
    useFetchLicenseDataAndSetStoreValues();
  const { isLoading: whoamiLoading, isError: whoamiError } = useFetchWhoami();

  const [user, setUser] = useAtom(userAtom);
  const [toasts, setToasts] = useAtom(toastsAtom);
  const [tokenExpires, setTokenExpires] = useAtom(tokenExpiresAtom);
  const [demoMode] = useAtom(demoModeAtom);

  const matches = useMatches();
  const path = usePathname();

  const windowsSize = useWindowSize().width;
  const isMobileOrTablet = windowsSize < 1180;
  const isMobile = windowsSize < 580;

  useEffect(() => {
    if (whoamiError || LicenseDataError) {
      if (['privacy-policy', 'authentication'].includes(path.split('/')[1]))
        return setIsLoading(false);
      else {
        Sentry.setUser(undefined);
        setUser(undefined);
        clearStorageWithExeptions();
        window.location.replace('/authentication/login');
      }
    }

    // after whoami is loaded and there is no error, there must be a user,
    // but it takes one render until the user is in the store
    if (whoamiLoading || LicenseDataLoading || !user) return;

    Sentry.setUser({
      id: user.id,
      username: user.firstName?.[0] + '.' + user.lastName?.[0],
    });
    setIsLoading(false);
  }, [
    whoamiLoading,
    whoamiError,
    user,
    setUser,
    LicenseDataLoading,
    LicenseDataError,
    path,
  ]);

  useEffect(() => {
    if (ready) {
      // set document language
      document.documentElement.lang = i18n.resolvedLanguage;
      setLangSetup(true);
    }
  }, [ready, i18n.resolvedLanguage, setLangSetup]);

  useEffect(() => {
    if (langSetup && !isLoading) {
      document.querySelector('#boot-screen').style.opacity = 0;

      setTimeout(() => {
        // hide loadingscreen, wait for the animation to finish
        document.body.style.overflow = 'auto';
        document.querySelector('#boot-screen').style.display = 'none';
      }, 700);
    } else {
      document.body.style.overflowX = 'hidden';
      document.body.style.overflowY = 'visible';
      document.querySelector('#boot-screen').style.display = 'flex';
    }
  }, [
    isLoading,
    demoMode,
    user,
    path,
    i18n.resolvedLanguage,
    queryClient,
    langSetup,
  ]);

  useEffect(() => {
    if (!tokenExpires || !user) return;
    // show an alert before the token expires
    // TODO: show a dialog with relogin option
    const now = new Date();

    const expires = Number(tokenExpires);

    const expiresInSeconds = expires - (now.getTime() / 1000).toFixed(0);

    const expiresInMs = expiresInSeconds * 1000;

    const expireDate = fromUnixTime(Number(tokenExpires));

    // check if the token will expires in less than 24 days, which is the max value for setTimeout
    if (expiresInMs < 2147480000) {
      // logout the user when the token expires
      setTimeout(() => {
        setUser(undefined);
        setTokenExpires(undefined);
        alert(
          'Your session has expired, please login again on the following page',
        );
        window.location.replace('/authentication/login');
      }, expiresInMs);

      // show an alert 10 minutes before the token expires
      // TODO: show a dialog with relogin option
      setTimeout(() => {
        console.log('Token will expire in 10 minutes');
        // Set the minutes to 10 minutes before the token expires
        expireDate.setMinutes(expireDate.getMinutes() - 10);

        // Get hours and minutes, adding a leading zero if necessary
        const hours = expireDate.getHours().toString().padStart(2, '0');
        const minutes = expireDate.getMinutes().toString().padStart(2, '0');

        alert(
          `Your Session will expire in 10 minutes (at ${hours}:${minutes}), please logout and login again`,
        );
      }, expiresInMs - 6000);
    }
  }, [tokenExpires, user, setUser, setTokenExpires]);

  // Reload page if localStorage changes from another tab
  useEffect(() => {
    function reloadPage(event) {
      if (!document.hidden) return; // check if the tab is hidden

      const { key, oldValue, newValue } = event;

      if (
        [
          'reload',
          'user',
          'demoMode',
          'X-Dev-Token',
          'currentAuditEntryId',
          'i18nextLng',
        ].includes(key) &&
        newValue !== oldValue
      ) {
        window.location.reload();
      }
    }

    // this event only fires when the localStorage is changed from another tab
    window.addEventListener('storage', reloadPage);

    return () => {
      window.removeEventListener('storage', reloadPage);
    };
  }, [setRenderKey]);

  useEffect(() => {
    // add padding so that the footer is not covered by the contact buttons
    document.getElementById('app').style.paddingBottom =
      isMobileOrTablet && demoMode ? (isMobile ? '64px' : '32px') : 0;
  }, [isMobileOrTablet, demoMode, isMobile]);

  if (path === '/report') return <Navigate to='/report/overview' />;

  if (!langSetup || isLoading) {
    return null;
  }

  if (
    user &&
    !user.hasPassword &&
    path !== '/authentication/change-password' &&
    !demoMode &&
    !window.location.origin.includes('admin')
  ) {
    return window.location.assign('/authentication/change-password');
  } else {
    const redirectPath = checkIfRouteIsAllowed(user, matches, demoMode);

    if (redirectPath && path !== redirectPath) {
      return window.location.assign(redirectPath);
    }
  }

  return (
    <ThemeProvider theme={theme} key={i18n.resolvedLanguage}>
      <CssBaseline />

      <Fragment key={renderKey}>
        {!user || path.startsWith('/authentication/') ? (
          <Outlet />
        ) : (
          <>
            <SidebarProvider>
              <MainLayout />
            </SidebarProvider>
            <FaceliftWelcomeBanner />
          </>
        )}
      </Fragment>
      {path.includes('privacy-policy') ? null : <PrivacyDialog />}

      {toasts.map((toast) => (
        <Notification
          key={toast.key}
          notification={toast}
          onExited={() =>
            setToasts((toasts) => toasts.filter((t) => t.key !== toast.key))
          }
        />
      ))}
    </ThemeProvider>
  );
}

export default BaseRoute;
